import type { ShoppingItemWriteInput } from '@recipes/contracts';
import {
  detectDictationEnd,
  formatQuantityAmount,
  parseCheckCommand,
  parseIngredientLine,
  parseShoppingUtterance,
  type CheckCommandItem,
} from '@recipes/domain';
import { Badge, Button, Input, cn } from '@recipes/ui';
import { Check, Mic, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig, useFeature, useProfile } from '@/features/shared/hooks';
import {
  usePushToTalk,
  type AutoStopOptions,
  type PushToTalk,
} from '@/features/speech/use-push-to-talk';
import { ProcessingIndicator } from '@/features/shared/processing-indicator';
import { useHoldOrTap } from '@/features/speech/use-hold-or-tap';
import { api } from '@/lib/api';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { currentLocale } from '@/lib/i18n';
import { signalDictationEnd } from '@/lib/speech/feedback';
import { speak, ttsSupported } from '@/lib/tts/speak';
import { toBcp47 } from '@/lib/speech/recognition';
import { parseQuickAdd } from './add-field';
import { useShoppingPreferences } from './hooks';

const HANDS_FREE_SECONDS = 3;
const AI_TIMEOUT_MS = 6000;
/** Patience while nothing was said at all (docs/partial/shopping-in-store-spec.md 5.1, D2). */
const INITIAL_SILENCE_MS = 5000;
const DEFAULT_MAX_SECONDS = 60;
/** Chips follow the interim text at most four times a second. */
const PREVIEW_INTERVAL_MS = 250;

export interface VoiceChip {
  key: string;
  input: ShoppingItemWriteInput;
  /** In check mode: the open item this chip stands for. */
  itemId?: string;
}

export interface ShoppingVoice {
  /** False when there is no way to recognise speech here – the button explains why. */
  available: boolean;
  /** The button's hold-or-tap handlers and whether it is held right now. */
  press: ReturnType<typeof useHoldOrTap>;
  enabled: boolean;
  ptt: PushToTalk;
  chips: VoiceChip[];
  /** "ich habe …" / "… erledigt": the chips are open items to check off, not things to add. */
  mode: 'add' | 'check';
  /** Check mode: names that were said but are not on the list. */
  unmatched: string[];
  /** The chips come from the AI model, not from the rule-based parser. */
  fromAi: boolean;
  improving: boolean;
  canImprove: boolean;
  /** Seconds until the chips are added automatically; null when no countdown runs. */
  countdown: number | null;
  nothingRecognised: boolean;
  /** The recording ends by itself – the button and the panel show what it is waiting for. */
  autoStop: boolean;
  /** A spoken "abbrechen" threw the last recording away. */
  discarded: boolean;
  improve: () => void;
  /** The user took over: no automatic add any more. */
  pause: () => void;
  updateChip: (key: string, line: string) => void;
  removeChip: (key: string) => void;
  addAll: () => void;
  cancel: () => void;
}

const chipOf = (input: ShoppingItemWriteInput): VoiceChip => ({ key: crypto.randomUUID(), input });

export const chipLabel = (input: ShoppingItemWriteInput): string => {
  const amount = formatQuantityAmount(
    {
      quantity: input.quantity ?? null,
      quantityMax: input.quantityMax ?? null,
      quantityText: input.quantityText ?? null,
      unitCode: input.unitCode ?? null,
      unitText: input.unitText ?? null,
    },
    currentLocale(),
  );
  return amount ? `${amount} ${input.name}` : input.name;
};

/**
 * Dictating shopping items (docs/partial/shopping-list-extensions-spec.md 5.6, 6.6). Splitting and
 * parsing happen on the device; the AI fallback only runs when the user switched it on, only
 * online, and never blocks: on any error or after a few seconds the rule-based chips stay.
 */
export function useShoppingVoice(options: {
  knownNames: string[];
  onAdd: (inputs: ShoppingItemWriteInput[], handsFree: boolean) => Promise<string[]> | void;
  /** The open items of the list – what a spoken "ich habe …" can check off. */
  openItems?: CheckCommandItem[];
  onCheck?: (items: CheckCommandItem[], handsFree: boolean) => Promise<string[]> | void;
}): ShoppingVoice {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const speechEnabled = useFeature('speech');
  const aiEnabled = useFeature('ai');
  const { data: config } = useConfig();
  const { data: profile } = useProfile();
  const preferences = useShoppingPreferences();
  const [chips, setChips] = useState<VoiceChip[]>([]);
  const [mode, setMode] = useState<'add' | 'check'>('add');
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [fromAi, setFromAi] = useState(false);
  const [improving, setImproving] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [nothingRecognised, setNothingRecognised] = useState(false);
  const [discarded, setDiscarded] = useState(false);
  const transcriptRef = useRef('');
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const aiAbort = useRef<AbortController | null>(null);

  const canImprove =
    preferences.aiVoiceParsing &&
    aiEnabled &&
    online &&
    Boolean(profile?.aiEnabled && profile.aiConsentAt);

  const startCountdown = useCallback(
    (count: number) => {
      if (preferences.handsFree && count > 0) setCountdown(HANDS_FREE_SECONDS);
    },
    [preferences.handsFree],
  );

  const runAi = useCallback(
    async (text: string, fallbackCount: number) => {
      aiAbort.current?.abort();
      const controller = new AbortController();
      aiAbort.current = controller;
      const timer = setTimeout(() => controller.abort(), AI_TIMEOUT_MS);
      setImproving(true);
      setCountdown(null);
      let count = fallbackCount;
      try {
        const result = await api.shopping.parseUtterance(
          { text, language: currentLocale() },
          controller.signal,
        );
        if (result.items.length && !controller.signal.aborted) {
          // Same normalisation as typed input: quantity, unit and name through the line parser.
          const next = result.items.map((item) =>
            chipOf(parseQuickAdd([item.quantity, item.unit, item.name].filter(Boolean).join(' '))),
          );
          setChips(next);
          setFromAi(true);
          setNothingRecognised(false);
          count = next.length;
        }
      } catch {
        // offline, timeout, not allowed, limit reached: the rule-based chips simply stay
      } finally {
        clearTimeout(timer);
        if (aiAbort.current === controller) {
          aiAbort.current = null;
          setImproving(false);
          startCountdown(count);
        }
      }
    },
    [startCountdown],
  );

  const handleTranscript = useCallback(
    (transcript: string) => {
      const text = transcript.trim();
      if (!text) return;
      transcriptRef.current = text;
      const command = optionsRef.current.onCheck
        ? parseCheckCommand(text, currentLocale(), optionsRef.current.openItems ?? [])
        : null;
      if (command) {
        setMode('check');
        setChips(
          command.matched.map((item) => ({
            key: item.id,
            itemId: item.id,
            input: { name: item.name },
          })),
        );
        setUnmatched(command.unmatched);
        setFromAi(false);
        setNothingRecognised(command.matched.length === 0 && command.unmatched.length === 0);
        startCountdown(command.matched.length);
        return;
      }
      setMode('add');
      setUnmatched([]);
      const parsed = parseShoppingUtterance(text, currentLocale(), optionsRef.current.knownNames);
      const next = parsed.items.map(({ source: _source, ...input }) => chipOf(input));
      setChips(next);
      setFromAi(false);
      setNothingRecognised(next.length === 0);
      if (parsed.unreliable && canImprove) void runAi(text, next.length);
      else startCountdown(next.length);
    },
    [canImprove, runAi, startCountdown],
  );

  const silenceMs = preferences.voiceSilenceSeconds * 1000;
  const maxMs = (config?.shopping?.voiceMaxSeconds ?? DEFAULT_MAX_SECONDS) * 1000;
  const heldRef = useRef<() => boolean>(() => false);
  const autoStop = useMemo<AutoStopOptions | undefined>(
    () =>
      preferences.voiceAutoStop
        ? {
            silenceMs,
            initialSilenceMs: INITIAL_SILENCE_MS,
            maxMs,
            isHeld: () => heldRef.current(),
            detectEnd: (transcript) =>
              detectDictationEnd(transcript, currentLocale(), {
                knownNames: optionsRef.current.knownNames,
              }),
            onEnd: ({ reason, text }) => {
              // The user is not looking: the end is heard and felt, whoever caused it.
              if (reason !== 'tap')
                signalDictationEnd(reason === 'cancel-word' ? 'discarded' : 'finished');
              if (reason === 'cancel-word') {
                setChips([]);
                setCountdown(null);
                setNothingRecognised(false);
                setDiscarded(true);
              } else if (!text) {
                setChips([]);
                setNothingRecognised(true);
              }
            },
          }
        : undefined,
    [maxMs, preferences.voiceAutoStop, silenceMs],
  );

  const ptt = usePushToTalk({
    language: currentLocale(),
    preferServerTranscription: profile?.speechPreferences.preferServerTranscription ?? false,
    serverTranscriptionAvailable:
      online && speechEnabled && (config?.speech.serverTranscription ?? false),
    context: 'dictation',
    onResult: handleTranscript,
    autoStop,
  });

  // With the browser recogniser the chips appear while speaking. They are a preview: the final
  // transcript replaces them, and nothing is added before the recording has ended.
  const listening = ptt.state === 'listening';
  // Hold to talk, or tap (user, 2026-09-24): holding records only while held, a tap runs on.
  const press = useHoldOrTap({ active: listening, start: ptt.start, stop: ptt.stop });
  heldRef.current = press.held;
  const interim = listening && autoStop ? ptt.transcript : '';
  const lastPreview = useRef(0);
  useEffect(() => {
    if (!interim) return;
    const render = () => {
      lastPreview.current = Date.now();
      const { text } = detectDictationEnd(interim, currentLocale(), {
        knownNames: optionsRef.current.knownNames,
      });
      const command = optionsRef.current.onCheck
        ? parseCheckCommand(text, currentLocale(), optionsRef.current.openItems ?? [])
        : null;
      if (command) {
        setMode('check');
        setChips(
          command.matched.map((item) => ({
            key: `preview-${item.id}`,
            itemId: item.id,
            input: { name: item.name },
          })),
        );
        return;
      }
      setMode('add');
      const parsed = parseShoppingUtterance(text, currentLocale(), optionsRef.current.knownNames);
      setChips(
        parsed.items.map(({ source: _source, ...input }, index) => ({
          key: `preview-${index}`,
          input,
        })),
      );
    };
    const wait = PREVIEW_INTERVAL_MS - (Date.now() - lastPreview.current);
    if (wait <= 0) {
      render();
      return;
    }
    const timer = setTimeout(render, wait);
    return () => clearTimeout(timer);
  }, [interim]);
  useEffect(() => {
    if (listening) setDiscarded(false);
  }, [listening]);

  const reset = useCallback(() => {
    aiAbort.current?.abort();
    aiAbort.current = null;
    setChips([]);
    setMode('add');
    setUnmatched([]);
    setFromAi(false);
    setImproving(false);
    setCountdown(null);
    setNothingRecognised(false);
    setDiscarded(false);
  }, []);

  const addAll = useCallback(
    (handsFree = false) => {
      const checking = mode === 'check';
      const targets = chips
        .filter((chip): chip is VoiceChip & { itemId: string } => !!chip.itemId)
        .map((chip) => ({ id: chip.itemId, name: chip.input.name }));
      const inputs = chips.map((chip) => chip.input);
      reset();
      if (checking ? !targets.length : !inputs.length) return;
      const run = checking
        ? optionsRef.current.onCheck?.(targets, handsFree)
        : optionsRef.current.onAdd(inputs, handsFree);
      void Promise.resolve(run).then((names) => {
        // Reading back is part of hands-free: the user is not looking at the screen.
        if (!handsFree || !names?.length || !ttsSupported()) return;
        if (!(profile?.speechPreferences.readAloudEnabled ?? true)) return;
        void speak(
          t(checking ? 'shopping.voice.readBackChecked' : 'shopping.voice.readBack', {
            items: names.join(', '),
          }),
          {
            lang: toBcp47(currentLocale()),
            rate: profile?.speechPreferences.speechRate,
            voiceName: profile?.speechPreferences.voiceName,
          },
        ).catch(() => undefined);
      });
    },
    [chips, mode, profile, reset, t],
  );

  const addAllRef = useRef(addAll);
  addAllRef.current = addAll;
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      addAllRef.current(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => () => aiAbort.current?.abort(), []);

  return {
    available: ptt.adapter !== null,
    press,
    enabled: speechEnabled && (profile?.speechPreferences.inputEnabled ?? true),
    ptt,
    chips,
    mode,
    unmatched,
    fromAi,
    improving,
    canImprove: canImprove && chips.length > 0 && !fromAi && mode === 'add',
    countdown,
    nothingRecognised,
    autoStop: autoStop !== undefined,
    discarded,
    improve: () => void runAi(transcriptRef.current, chips.length),
    pause: () => setCountdown(null),
    // Any intervention stops the countdown.
    updateChip: (key, line) => {
      setCountdown(null);
      setChips((prev) =>
        prev.flatMap((chip) => {
          if (chip.key !== key) return [chip];
          const name = parseIngredientLine(line.trim()).name;
          return name ? [{ key, input: parseQuickAdd(line.trim()) }] : [];
        }),
      );
    },
    removeChip: (key) => {
      setCountdown(null);
      setChips((prev) => prev.filter((chip) => chip.key !== key));
    },
    addAll: () => addAll(false),
    cancel: () => {
      ptt.cancel();
      reset();
    },
  };
}

/**
 * The microphone button: in the add field on a desktop, and on a phone a round button at the
 * bottom right, stacked with the scanner and the chat (user 2026-10-01). `fab` renders the
 * round one with the given `bottom-…` class; it stays away when speech is unavailable.
 */
export function VoiceButton({
  voice,
  fab = false,
  bottomClass,
  className,
}: {
  voice: ShoppingVoice;
  fab?: boolean;
  bottomClass?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!voice.enabled) return null;
  const { ptt } = voice;
  const listening = ptt.state === 'listening';
  if (!voice.available)
    return fab ? null : (
      <Button
        type="button"
        variant="outline"
        className={className}
        disabled
        aria-label={t('shopping.voice.unavailable')}
        title={t('shopping.voice.unavailable')}
        data-testid="shopping-voice-unavailable"
      >
        <Mic aria-hidden />
      </Button>
    );
  return (
    <Button
      type="button"
      variant={listening ? 'default' : 'outline'}
      size={fab ? 'icon' : undefined}
      aria-pressed={listening}
      aria-label={listening ? t('shopping.voice.stop') : t('shopping.voice.start')}
      title={`${voice.autoStop ? t('shopping.voice.hintAutoStop') : t('shopping.voice.hint')} ${t('shopping.voice.hintCheck')} ${t('shopping.voice.holdHint')}`}
      className={cn(
        'relative select-none touch-manipulation',
        fab && 'fixed right-4 z-20 size-14 rounded-full bg-background shadow-lg md:hidden',
        fab && listening && 'bg-primary',
        fab && bottomClass,
        className,
      )}
      loading={ptt.state === 'processing'}
      // Tap to start and stop, or hold to talk (release ends it) – both, since 2026-09-24.
      {...voice.press.handlers}
      data-testid={fab ? 'shopping-voice-fab' : 'shopping-voice'}
    >
      <Mic aria-hidden className={fab ? 'size-6' : undefined} />
      {listening && ptt.silence && <SilenceRing silence={ptt.silence} />}
    </Button>
  );
}

/**
 * Runs down while nothing is said, so the automatic stop is no surprise. The animation restarts
 * with every word (a new `endsAt`); with reduced motion the ring is simply there.
 */
function SilenceRing({ silence }: { silence: NonNullable<PushToTalk['silence']> }) {
  const circumference = 2 * Math.PI * 18;
  return (
    <svg
      key={silence.endsAt}
      viewBox="0 0 40 40"
      aria-hidden
      className="pointer-events-none absolute inset-0 m-auto size-10 -rotate-90"
      data-testid="shopping-voice-ring"
    >
      <circle
        cx="20"
        cy="20"
        r="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeDasharray={circumference}
        className="motion-safe:animate-silence-ring"
        style={{
          animationDuration: `${silence.totalMs}ms`,
          ['--ring-length' as string]: `${circumference}`,
        }}
      />
    </svg>
  );
}

/** The microphone level of the server adapter: "is it still listening?" at a glance. */
function LevelMeter({ level }: { level: number }) {
  const bars = [0.15, 0.35, 0.55, 0.75, 0.95];
  return (
    <span
      className="inline-flex h-4 items-end gap-0.5 align-middle"
      aria-hidden
      data-testid="shopping-voice-level"
    >
      {bars.map((bar, index) => (
        <span
          key={bar}
          className={`w-1 rounded-sm ${level >= bar ? 'bg-primary' : 'bg-muted-foreground/30'}`}
          style={{ height: `${40 + index * 15}%` }}
        />
      ))}
    </span>
  );
}

export function VoicePanel({ voice }: { voice: ShoppingVoice }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const { ptt, chips } = voice;
  const listening = ptt.state === 'listening' || ptt.state === 'processing';
  if (
    !listening &&
    !chips.length &&
    !voice.nothingRecognised &&
    !voice.discarded &&
    ptt.state !== 'error'
  )
    return null;

  return (
    <div
      className="mb-4 space-y-2 rounded-lg border bg-muted/30 p-3"
      role="group"
      aria-label={t('shopping.voice.panel')}
      data-testid="shopping-voice-panel"
    >
      {listening && (
        <p className="text-sm" aria-live="polite">
          <span className="font-medium">{t('shopping.voice.listening')}</span>{' '}
          {ptt.level !== null && <LevelMeter level={ptt.level} />}{' '}
          <span className="text-muted-foreground">{ptt.transcript}</span>
          {voice.autoStop && ptt.state === 'listening' && (
            <span className="block text-xs text-muted-foreground">
              {ptt.adapter === 'server'
                ? t('shopping.voice.autoStopSilenceOnly')
                : t('shopping.voice.autoStopHint')}
            </span>
          )}
        </p>
      )}
      {voice.discarded && !listening && (
        <p
          className="text-sm text-muted-foreground"
          role="status"
          data-testid="shopping-voice-discarded"
        >
          {t('shopping.voice.discarded')}
        </p>
      )}
      {ptt.state === 'error' && (
        <p className="text-sm text-destructive" role="alert">
          {t('shopping.voice.error')}
        </p>
      )}
      {voice.nothingRecognised && !listening && (
        <p className="text-sm text-muted-foreground" role="status">
          {t('shopping.voice.nothing')}
        </p>
      )}
      {voice.mode === 'check' && !listening && (
        <p className="text-sm" data-testid="shopping-voice-check">
          <Check className="mr-1 inline size-4" aria-hidden />
          {t('shopping.voice.checkRecognised', { count: chips.length })}
          {voice.unmatched.length > 0 && (
            <span className="block text-xs text-muted-foreground">
              {t('shopping.voice.notOnList', { items: voice.unmatched.join(', ') })}
            </span>
          )}
        </p>
      )}
      {chips.length > 0 && (
        <>
          <ul className="flex flex-wrap gap-2" aria-label={t('shopping.voice.recognised')}>
            {chips.map((chip) => (
              <li key={chip.key} data-testid="shopping-voice-chip">
                {chip.itemId ? (
                  <span className="inline-flex items-center gap-1 rounded-full border bg-background py-1 pl-3 pr-1 text-sm">
                    <span className="min-h-[32px] leading-8">{chip.input.name}</span>
                    <button
                      type="button"
                      className="flex size-8 items-center justify-center rounded-full hover:bg-muted"
                      onClick={() => voice.removeChip(chip.key)}
                      aria-label={t('shopping.voice.removeChip', { name: chip.input.name })}
                    >
                      <X aria-hidden className="size-4" />
                    </button>
                  </span>
                ) : editing === chip.key ? (
                  <form
                    className="flex gap-1"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (draft !== chipLabel(chip.input)) voice.updateChip(chip.key, draft);
                      setEditing(null);
                    }}
                  >
                    <Input
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => {
                        if (draft !== chipLabel(chip.input)) voice.updateChip(chip.key, draft);
                        setEditing(null);
                      }}
                      aria-label={t('shopping.voice.editChip')}
                      className="h-9 w-44"
                    />
                  </form>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full border bg-background py-1 pl-3 pr-1 text-sm">
                    <button
                      type="button"
                      className="min-h-[32px]"
                      onClick={() => {
                        setDraft(chipLabel(chip.input));
                        setEditing(chip.key);
                        voice.pause();
                      }}
                      aria-label={t('shopping.voice.editNamed', { name: chipLabel(chip.input) })}
                    >
                      {chipLabel(chip.input)}
                    </button>
                    <button
                      type="button"
                      className="flex size-8 items-center justify-center rounded-full hover:bg-muted"
                      onClick={() => voice.removeChip(chip.key)}
                      aria-label={t('shopping.voice.removeChip', { name: chip.input.name })}
                    >
                      <X aria-hidden className="size-4" />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
          {/* While the recording runs the chips are a preview of what is being understood. */}
          <div
            className={ptt.state === 'listening' ? 'hidden' : 'flex flex-wrap items-center gap-2'}
          >
            <Button type="button" size="sm" onClick={voice.addAll} data-testid="shopping-voice-add">
              {voice.mode === 'check'
                ? voice.countdown !== null
                  ? t('shopping.voice.checkAllIn', {
                      count: chips.length,
                      seconds: voice.countdown,
                    })
                  : t('shopping.voice.checkAll', { count: chips.length })
                : voice.countdown !== null
                  ? t('shopping.voice.addAllIn', { count: chips.length, seconds: voice.countdown })
                  : t('shopping.voice.addAll', { count: chips.length })}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={voice.cancel}>
              {t('common.cancel')}
            </Button>
            {voice.canImprove && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                loading={voice.improving}
                onClick={voice.improve}
                data-testid="shopping-voice-improve"
              >
                <Sparkles aria-hidden /> {t('shopping.voice.improve')}
              </Button>
            )}
            {voice.improving && (
              <ProcessingIndicator
                compact
                className="basis-full"
                label={t('shopping.voice.improving')}
              />
            )}
            {voice.fromAi && <Badge variant="secondary">{t('shopping.voice.ai')}</Badge>}
          </div>
        </>
      )}
    </div>
  );
}
